import * as Yup from 'yup';

import {
  MAX_NUMBER_MESSAGE,
  PACKAGE_MIN_COUNT_MESSAGE,
  SAME_QUOTA_TYPE_ERROR_MESSAGE,
} from '../constants/validationMessages';
import { type IAdditionalFieldConfig, type IFeatureFlagsResponse } from '../types/response';
import { DataType, OperationType, QuotaType } from '../types/enums';
import { type TValidationConfig } from '../types/configs';
import { FieldPrefix } from '../constants/fieldPrefixes';
import { itemConfigNames } from '../types/configNames';
import { ItemsTabFields } from '../types/fieldEnums';

import { getNumberValidation, getObjectValidation, getStringValidation } from './validations';
import getAdditionalFieldsSchemaShape from './getAdditionalFieldsSchemaShape';
import i18n from '../../../configs/i18n';

interface IGetItemSchema {
  operation?: string;
  itemAdditionalFields?: IAdditionalFieldConfig[][];
  validationConfig: TValidationConfig;
  featureFlags: IFeatureFlagsResponse;
}

const getItemSchema = ({ operation, itemAdditionalFields, validationConfig, featureFlags }: IGetItemSchema) => {
  const additionalFieldsArray = itemAdditionalFields?.flat(2) ?? [];

  const additionalFields = additionalFieldsArray.filter((field) => field.dataType !== DataType.OPTIONS_FROM_DATABASE);
  const dbOptionAdditionalFields = additionalFieldsArray.filter(
    (field) => field.dataType === DataType.OPTIONS_FROM_DATABASE
  );

  return Yup.object().shape({
    commodityCode: getObjectValidation({
      field: itemConfigNames[ItemsTabFields.commodityCode],
      operation,
    }),
    countryOfOriginCode: getObjectValidation({
      field: itemConfigNames[ItemsTabFields.countryOfOriginCode],
      operation,
    }),
    commercialDescription: getStringValidation({
      field: itemConfigNames[ItemsTabFields.commercialDescription],
      operation,
      validationConfig,
    }),
    manufacturerName: getStringValidation({
      field: itemConfigNames[ItemsTabFields.manufacturerName],
      operation,
      validationConfig,
    }),
    packageNumber: getNumberValidation({
      field: itemConfigNames[ItemsTabFields.packageNumber],
      operation,
      validationConfig,
    }),
    packageTypeCode: getObjectValidation({ field: itemConfigNames[ItemsTabFields.packageTypeCode], operation }),
    packageMark: getStringValidation({
      field: itemConfigNames[ItemsTabFields.packageMark],
      operation,
      validationConfig,
    }),
    grossMass: getNumberValidation({ field: itemConfigNames[ItemsTabFields.grossMass], operation, validationConfig }),
    netMass: getNumberValidation({
      field: itemConfigNames[ItemsTabFields.netMass],
      operation,
      validationConfig,
    }).when('grossMass', ([grossMass], schema) => {
      const max = typeof grossMass === 'number' ? grossMass : 0;

      return schema.max(max, () => i18n.t(MAX_NUMBER_MESSAGE, { max }) as string);
    }),
    itemInvoiceAmountInForeignCurrency: getNumberValidation({
      field: itemConfigNames[ItemsTabFields.itemInvoiceAmountInForeignCurrency],
      operation,
      validationConfig,
    }),
    itemInvoiceAmountInNationalCurrency: getNumberValidation({
      field: itemConfigNames[ItemsTabFields.itemInvoiceAmountInNationalCurrency],
      operation,
      validationConfig,
    }),
    quotaType: getStringValidation({
      field: itemConfigNames[ItemsTabFields.quotaType],
      operation,
    }),

    quotaType2: featureFlags.doubleQuotaEnabled
      ? getStringValidation({
          field: itemConfigNames[ItemsTabFields.quotaType2],
          operation,
        }).notOneOf([Yup.ref('quotaType')], SAME_QUOTA_TYPE_ERROR_MESSAGE)
      : Yup.mixed().notRequired(),

    requestedAmount: Yup.mixed().when('quotaType', ([quotaType], schema) => {
      if (quotaType !== QuotaType.UNLIMITED) {
        return getNumberValidation({
          field: itemConfigNames[ItemsTabFields.requestedAmount],
          operation,
          validationConfig,
        });
      }

      return schema.notRequired();
    }),
    approvedAmount: Yup.mixed().when('quotaType', ([quotaType], schema) => {
      if (quotaType !== QuotaType.UNLIMITED) {
        return getNumberValidation({
          field: itemConfigNames[ItemsTabFields.approvedAmount],
          operation,
          validationConfig,
        });
      }

      return schema.notRequired();
    }),
    remainingAmount: Yup.mixed().when('quotaType', ([quotaType], schema) => {
      if (quotaType !== QuotaType.UNLIMITED) {
        return getNumberValidation({
          field: itemConfigNames[ItemsTabFields.remainingAmount],
          operation,
          validationConfig,
        });
      }

      return schema.notRequired();
    }),
    additionalFields: Yup.object().shape(
      additionalFields.length
        ? getAdditionalFieldsSchemaShape(additionalFields, FieldPrefix.item, operation, validationConfig)
        : {}
    ),
    dbOptionAdditionalFields: Yup.object().shape(
      dbOptionAdditionalFields.length
        ? getAdditionalFieldsSchemaShape(dbOptionAdditionalFields, FieldPrefix.item, operation, validationConfig)
        : {}
    ),
    requestedUomCode: Yup.object().when('quotaType', ([quotaType], schema) => {
      if (quotaType === QuotaType.UOM) {
        return getObjectValidation({
          field: itemConfigNames[ItemsTabFields.requestedUomCode],
          operation,
        });
      }

      return schema.notRequired();
    }),
    approvedUomCode: Yup.object().when('quotaType', ([quotaType], schema) => {
      if (quotaType === QuotaType.UOM) {
        return getObjectValidation({
          field: itemConfigNames[ItemsTabFields.approvedUomCode],
          operation,
        });
      }

      return schema.notRequired();
    }),
    packages: Yup.array().when([], {
      is: () =>
        featureFlags.packageManagementTableEnabled &&
        !!operation &&
        ![OperationType.STORE, OperationType.UPDATE_STORE, OperationType.DELETE].includes(operation as OperationType),
      then: (schema) => schema.min(1, PACKAGE_MIN_COUNT_MESSAGE),
    }),
  });
};

export default getItemSchema;
